import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Collapse, useTheme } from '@mui/material';
import { IconAlertTriangle } from '@tabler/icons-react';

import { Tooltip, Typography } from '@zendesk/forethought-elements';
import { GenericTranscriptComponent } from '../../ConversationTranscript/RevampConversationTranscript';
import { TranscriptComponent } from '../../ConversationTranscript/types';
import {
  useGetComponentTopPadding,
  useGetMessageBubblePosition,
} from '../../ConversationTranscript/utils';
import { formatTimestamp } from '../../helpers';
import {
  auditErrorCount,
  auditErrorEvents,
  AuditEvent,
  ConversationAudit,
  erroredExchangeIndexes,
  formatLatency,
  isErrorAuditEvent,
} from './auditData';
import { AuditMessage } from './AuditMessage';
import { buildExchangeItems, EventGroupItem } from './buildExchangeItems';
import { EvidenceTimeline } from './EvidenceTimeline';
import { isAuditMessage, isDivider, splitIntoExchanges } from './exchanges';

// One collapsible run of consecutive events, placed inline at the point in the
// conversation where it actually happened. Nothing where there are no events, a
// grey count where the run is healthy, a red-tinted count where it is not. The
// latency is the wall-clock gap between the message above and the message below
// the run, when both exist.
const EventGroup = ({
  errorOrdinalByEvent,
  errorTotal,
  events,
  isOpen,
  latencyMs,
  onJumpToError,
  onToggle,
}: {
  events: AuditEvent[];
  errorOrdinalByEvent: WeakMap<AuditEvent, number>;
  errorTotal: number;
  isOpen: boolean;
  latencyMs: number | null;
  onJumpToError: (targetOrdinal: number) => void;
  onToggle: () => void;
}) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const { palette } = useTheme();
  const errorCount = events.filter(isErrorAuditEvent).length;
  const hasError = errorCount > 0;

  if (events.length === 0) {
    return null;
  }

  const separatorColor = hasError
    ? palette.colors.red[400]
    : palette.colors.grey[500];
  const textColor = hasError
    ? palette.colors.red[700]
    : palette.colors.grey[600];

  return (
    <Box
      sx={{
        bgcolor: hasError ? palette.colors.red[100] : undefined,
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        px: hasError ? '12px' : 0,
        py: hasError ? '8px' : '4px',
      }}
    >
      <Box
        aria-expanded={isOpen}
        component='button'
        onClick={onToggle}
        sx={{
          alignItems: 'center',
          alignSelf: 'flex-start',
          background: 'none',
          border: 0,
          cursor: 'pointer',
          display: 'flex',
          flexWrap: 'wrap',
          font: 'inherit',
          gap: '6px',
          p: 0,
          // Indent the header alone: the timeline below starts at the exchange
          // edge so its dots share the avatar column.
          pl: hasError ? 0 : '32px',
          textAlign: 'left',
        }}
      >
        {hasError && (
          <>
            <IconAlertTriangle color={palette.colors.red[500]} size={13} />
            <Typography color={palette.colors.red[700]} variant='font12Medium'>
              {t('conversationAudit.errorCount', { count: errorCount })}
            </Typography>
            <Typography color={separatorColor} variant='font12'>
              ·
            </Typography>
          </>
        )}
        <Typography color={textColor} variant='font12'>
          {t('conversationAudit.eventCount', { count: events.length })}
        </Typography>
        {latencyMs !== null && (
          <>
            <Typography color={separatorColor} variant='font12'>
              ·
            </Typography>
            <Typography color={textColor} variant='font12'>
              {formatLatency(latencyMs)}
            </Typography>
          </>
        )}
        <Typography color={separatorColor} variant='font12'>
          ·
        </Typography>
        <Box sx={{ textDecoration: 'underline' }}>
          <Typography color={textColor} variant='font12Medium'>
            {isOpen
              ? t('conversationAudit.hideDetails')
              : t('conversationAudit.showDetails')}
          </Typography>
        </Box>
      </Box>
      <Collapse in={isOpen} timeout={200}>
        <EvidenceTimeline
          errorOrdinalByEvent={errorOrdinalByEvent}
          errorTotal={errorTotal}
          events={events}
          onJumpToError={onJumpToError}
        />
      </Collapse>
    </Box>
  );
};

const ErrorsBadge = ({
  count,
  onClick,
}: {
  count: number;
  onClick: () => void;
}) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const { palette } = useTheme();

  if (count === 0) {
    return null;
  }

  return (
    <Box
      component='button'
      onClick={onClick}
      sx={{
        alignItems: 'center',
        bgcolor: palette.colors.white,
        border: `1px solid ${palette.colors.red[500]}`,
        borderRadius: '16px',
        cursor: 'pointer',
        display: 'flex',
        font: 'inherit',
        gap: '4px',
        height: '32px',
        px: '12px',
      }}
    >
      <IconAlertTriangle color={palette.colors.red[500]} size={13} />
      <Typography color={palette.colors.red[700]} variant='font12Medium'>
        {t('conversationAudit.errorsWithCount', { count })}
      </Typography>
    </Box>
  );
};

// How much of the audit the transcript shows, least to most detail:
// - 'collapsed' (default): messages with each run of events between them,
//   collapsed to its summary line.
// - 'messages-only': messages alone — no event rows, no dividers.
// - 'expanded': messages with every event run between them fully open.
export type TranscriptDisplayMode = 'collapsed' | 'messages-only' | 'expanded';

const DISPLAY_MODES: TranscriptDisplayMode[] = [
  'messages-only',
  'collapsed',
  'expanded',
];

const segmentRadius = (index: number, total: number) =>
  index === 0 ? '16px 0 0 16px' : index === total - 1 ? '0 16px 16px 0' : '0';

// The transcript's tri-state control: a segmented pill, one segment per display
// mode, with the active mode filled dark. Labels stay terse; the tooltip
// carries the full description.
const DisplayModeToggle = ({
  mode,
  onChange,
}: {
  mode: TranscriptDisplayMode;
  onChange: (mode: TranscriptDisplayMode) => void;
}) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const { palette } = useTheme();
  const labels: Record<TranscriptDisplayMode, string> = {
    collapsed: t('conversationAudit.displayModeEventsCollapsed'),
    expanded: t('conversationAudit.displayModeEventsExpanded'),
    'messages-only': t('conversationAudit.displayModeMessagesOnly'),
  };
  const hints: Record<TranscriptDisplayMode, string> = {
    collapsed: t('conversationAudit.displayModeEventsCollapsedHint'),
    expanded: t('conversationAudit.displayModeEventsExpandedHint'),
    'messages-only': t('conversationAudit.displayModeMessagesOnlyHint'),
  };

  return (
    <Box
      role='group'
      sx={{
        bgcolor: palette.colors.white,
        border: `1px solid ${palette.colors.slate[200]}`,
        borderRadius: '16px',
        display: 'flex',
      }}
    >
      {DISPLAY_MODES.map((value, index) => {
        const isActive = mode === value;
        // Round the ends on the buttons themselves: each segment is wrapped in
        // the tooltip's own div, which doesn't inherit the pill's height, so
        // the container's radius+clip can't shape the fill.
        const radius = segmentRadius(index, DISPLAY_MODES.length);
        return (
          <Tooltip key={value} placement='top' tooltipContent={hints[value]}>
            <Box
              aria-pressed={isActive}
              component='button'
              onClick={() => onChange(value)}
              sx={{
                '&:hover': isActive
                  ? undefined
                  : { bgcolor: palette.colors.grey[100] },
                alignItems: 'center',
                bgcolor: isActive ? palette.colors.grey[800] : 'transparent',
                border: 0,
                borderRadius: radius,
                cursor: 'pointer',
                display: 'flex',
                font: 'inherit',
                height: '30px',
                px: '10px',
              }}
            >
              <Typography
                color={
                  isActive ? palette.colors.white : palette.colors.grey[700]
                }
                variant='font12Medium'
              >
                {labels[value]}
              </Typography>
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
};

const groupKey = (exchangeIndex: number, groupOrdinal: number) =>
  `${exchangeIndex}:${groupOrdinal}`;

export const AuditTranscript = ({
  audit,
  onRevealConsumed,
  reveal,
  transcriptComponents,
}: {
  audit: ConversationAudit;
  onRevealConsumed: () => void;
  // nonce 0 is inert; a positive nonce is one reveal aimed at `exchangeIndex`,
  // landing on the `errorOrdinal`-th error of the conversation when set.
  reveal: { errorOrdinal?: number; exchangeIndex: number; nonce: number };
  transcriptComponents: TranscriptComponent[];
}) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const { palette } = useTheme();
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const [displayMode, setDisplayMode] =
    useState<TranscriptDisplayMode>('collapsed');
  const [highlightedExchange, setHighlightedExchange] = useState(-1);
  // The turn the reveal effect asked us to scroll to. The nonce makes every
  // jump a new value, so a repeat jump to the same turn re-triggers the scroll
  // effect below instead of no-op-ing on an unchanged target.
  const [scrollTarget, setScrollTarget] = useState<{
    exchangeIndex: number;
    nonce: number;
    errorIndex?: number;
  } | null>(null);
  const exchangeRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const getMessageBubblePosition =
    useGetMessageBubblePosition(transcriptComponents);
  const getComponentTopPadding =
    useGetComponentTopPadding(transcriptComponents);

  const exchanges = useMemo(
    () => splitIntoExchanges(transcriptComponents),
    [transcriptComponents],
  );

  // Each exchange's messages and events merged into one chronological stream,
  // so a turn reads as it happened — message, the events that led to the reply,
  // the reply — rather than every message followed by every event at the foot.
  const exchangeItems = useMemo(
    () =>
      exchanges.map(exchange =>
        buildExchangeItems(
          exchange.components,
          audit.exchanges[exchange.exchangeIndex]?.events ?? [],
        ),
      ),
    [exchanges, audit],
  );

  const errorCount = auditErrorCount(audit);

  // Every error in the conversation in the order it failed, paired with its
  // exchange, so the per-error "Error X/Y" pager can number each row and jump to
  // the next one regardless of which turn or event group it lives in.
  const errorEvents = useMemo(() => auditErrorEvents(audit), [audit]);
  const errorOrdinalByEvent = useMemo(() => {
    const map = new WeakMap<AuditEvent, number>();
    errorEvents.forEach(({ event }, index) => map.set(event, index));
    return map;
  }, [errorEvents]);
  const errorTotal = errorEvents.length;
  const jumpNonce = useRef(0);

  // Open every event group in the next error's turn, highlight it, and scroll
  // that specific error row into view. The scroll is deferred until the Collapse
  // opens — the same effect the rollup jump uses — so the row is measured at its
  // final position rather than the collapsed one.
  const jumpToError = (targetOrdinal: number) => {
    const target = errorEvents[targetOrdinal];
    if (!target) {
      return;
    }
    const keys = (exchangeItems[target.exchangeIndex] ?? [])
      .filter((item): item is EventGroupItem => item.type === 'event-group')
      .map(item => groupKey(target.exchangeIndex, item.groupOrdinal));
    // A jump from transcript-only mode lands in the default details view; an
    // expanded transcript stays expanded, so the merge below can't close the
    // groups the mode had opened.
    setDisplayMode(previous =>
      previous === 'messages-only' ? 'collapsed' : previous,
    );
    setOpenGroups(previous => new Set([...previous, ...keys]));
    setHighlightedExchange(target.exchangeIndex);
    jumpNonce.current += 1;
    setScrollTarget({
      errorIndex: targetOrdinal,
      exchangeIndex: target.exchangeIndex,
      nonce: jumpNonce.current,
    });
  };

  const toggleGroup = (key: string) =>
    setOpenGroups(previous => {
      const next = new Set(previous);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

  // Switching modes resets the manual open/closed set to the new mode's
  // default, so the control is always a whole-transcript action; within any
  // mode, individual groups still open and close by hand.
  const changeDisplayMode = (mode: TranscriptDisplayMode) => {
    setDisplayMode(mode);
    if (mode === 'expanded') {
      setOpenGroups(
        new Set(
          exchangeItems.flatMap((items, exchangeIndex) =>
            items
              .filter(
                (item): item is EventGroupItem => item.type === 'event-group',
              )
              .map(item => groupKey(exchangeIndex, item.groupOrdinal)),
          ),
        ),
      );
    } else {
      setOpenGroups(new Set());
    }
  };

  // A jump from the error rollup expands every event group in the failing turn
  // and highlights it. The scroll itself is deferred to the effect below: the
  // groups open through a 200ms Collapse, so the error row is not at its final
  // position until that animation settles, and scrolling any sooner measures the
  // collapsed layout and lands at the top of the turn. A nonce of 0 is inert,
  // which keeps the drawer still on open and on every remount after a tab switch.
  useEffect(() => {
    if (reveal.nonce === 0) {
      return;
    }
    onRevealConsumed();
    const target =
      reveal.exchangeIndex >= 0
        ? reveal.exchangeIndex
        : erroredExchangeIndexes(audit)[0];
    if (target === undefined || target < 0) {
      return;
    }
    const keys = (exchangeItems[target] ?? [])
      .filter((item): item is EventGroupItem => item.type === 'event-group')
      .map(item => groupKey(target, item.groupOrdinal));
    setDisplayMode(previous =>
      previous === 'messages-only' ? 'collapsed' : previous,
    );
    setOpenGroups(previous => new Set([...previous, ...keys]));
    setHighlightedExchange(target);
    setScrollTarget({
      errorIndex: reveal.errorOrdinal,
      exchangeIndex: target,
      nonce: reveal.nonce,
    });
  }, [audit, reveal, onRevealConsumed, exchangeItems]);

  // Scroll the failing turn's error into view. Runs after the reveal/jump opens
  // the groups; the wait lets the Collapse finish expanding before the error row
  // is measured. Without it, scrollIntoView sees the collapsed layout and lands
  // at the top of the turn. A jump naming an errorIndex (the rollup's Turn
  // button, the per-error pager) centers that specific row; one without lands
  // the turn's last error at the bottom of the drawer.
  useEffect(() => {
    if (!scrollTarget) {
      return;
    }
    const container = exchangeRefs.current[scrollTarget.exchangeIndex];
    if (!container) {
      return;
    }
    const timer = setTimeout(() => {
      let element: HTMLElement | null = null;
      if (typeof scrollTarget.errorIndex === 'number') {
        element = container.querySelector<HTMLElement>(
          `[data-audit-error-index="${scrollTarget.errorIndex}"]`,
        );
      }
      if (!element) {
        const errorElements =
          container.querySelectorAll<HTMLElement>('[data-audit-error]');
        element = errorElements[errorElements.length - 1] ?? container;
      }
      element.scrollIntoView({
        behavior: 'smooth',
        block: scrollTarget.errorIndex === undefined ? 'end' : 'center',
      });
    }, 220);
    return () => clearTimeout(timer);
  }, [scrollTarget]);

  // The highlight is a pointer, not a state — it fades once it has been seen.
  useEffect(() => {
    if (highlightedExchange < 0) {
      return;
    }
    const timer = setTimeout(() => setHighlightedExchange(-1), 2400);
    return () => clearTimeout(timer);
  }, [highlightedExchange]);

  return (
    <Box
      sx={{
        border: `1px solid ${palette.colors.slate[200]}`,
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        p: '16px',
      }}
    >
      {transcriptComponents.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            justifyContent: 'flex-end',
          }}
        >
          <DisplayModeToggle mode={displayMode} onChange={changeDisplayMode} />
          <ErrorsBadge count={errorCount} onClick={() => jumpToError(0)} />
        </Box>
      )}

      {transcriptComponents.length > 0 && (
        <Box sx={{ pb: '4px', textAlign: 'center' }}>
          <Typography color={palette.colors.grey[600]} variant='font12'>
            {t('conversationAudit.conversationStarted', {
              timestamp: formatTimestamp(
                transcriptComponents[0].timestamp + 'Z',
              ),
            })}
          </Typography>
        </Box>
      )}

      {exchanges.map((exchange, position) => {
        const items = exchangeItems[position];
        return (
          <Box
            key={exchange.exchangeIndex}
            ref={(element: HTMLDivElement | null) => {
              exchangeRefs.current[exchange.exchangeIndex] = element;
            }}
            sx={{
              borderRadius: '12px',
              boxShadow:
                highlightedExchange === exchange.exchangeIndex
                  ? `0 0 0 2px ${palette.colors.red[500]}`
                  : '0 0 0 0 transparent',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              m: '-8px',
              p: '8px',
              transition: 'box-shadow 300ms ease',
            }}
          >
            {items.map(item => {
              if (item.type === 'event-group') {
                if (displayMode === 'messages-only') {
                  return null;
                }
                const key = groupKey(exchange.exchangeIndex, item.groupOrdinal);
                return (
                  <EventGroup
                    errorOrdinalByEvent={errorOrdinalByEvent}
                    errorTotal={errorTotal}
                    events={item.events}
                    isOpen={openGroups.has(key)}
                    key={`group-${key}`}
                    latencyMs={item.latencyMs}
                    onJumpToError={jumpToError}
                    onToggle={() => toggleGroup(key)}
                  />
                );
              }

              const { component, index } = item;
              if (displayMode === 'messages-only' && isDivider(component)) {
                return null;
              }
              return (
                <Fragment key={index}>
                  {isAuditMessage(component) ? (
                    <AuditMessage component={component} />
                  ) : (
                    <GenericTranscriptComponent
                      hideTimestampTooltip={isDivider(component)}
                      messageBubblePosition={getMessageBubblePosition(index)}
                      nextTranscriptComponent={transcriptComponents[index + 1]}
                      topPadding={getComponentTopPadding(index)}
                      transcriptComponent={component}
                    />
                  )}
                </Fragment>
              );
            })}
          </Box>
        );
      })}
    </Box>
  );
};
